/* eslint-disable no-unused-vars */
import { CartItem } from "./types";
import { LocalStorageKey } from "@/utils/storageKeys";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { ReadonlyURLSearchParams } from "next/navigation";
import { CartType } from "@/constants/enum";

/**
 * Base abstract class for representing a shopping cart.
 *
 * This class provides common functionality for managing cart items, such as adding,
 * removing, and updating items, as well as calculating totals. It serves as the
 * foundation for vertical-specific cart implementations.
 *
 * @template T - The type of items stored in the cart, extending `CartItem`.
 */
export abstract class Cart<T extends CartItem = CartItem> {
  /** Unique identifier for the cart instance. */
  public id: string;
  /** The identifier of the merchant associated with this cart. */
  public merchantId: string;
  /** The list of items currently in the cart. */
  public items: T[];

  /**
   * Creates a new Cart instance.
   *
   * @param data - Optional initial data to populate the cart.
   */
  constructor(data?: any) {
    this.id = data?.id || `cart_${Date.now()}`;
    this.merchantId = data?.merchantId || "";
    this.items = data?.items || [];
  }

  /**
   * Adds an item to the cart.
   *
   * If an item with the same key already exists, its quantity is incremented.
   * Otherwise, the new item is pushed to the items array.
   *
   * @param item - The item to add to the cart.
   */
  addItem(item: T): void {
    const existingItem = this.items.find((i) => i.key === item.key);
    if (existingItem) {
      existingItem.value.quantity += item.value.quantity;
    } else {
      this.items.push(item);
    }
  }

  /**
   * Removes an item from the cart by its unique key.
   *
   * @param itemId - The unique key of the item to remove.
   */
  removeItem(itemId: string): void {
    this.items = this.items.filter((item) => item.key !== itemId);
  }

  /**
   * Updates the quantity of a specific item in the cart.
   *
   * If the quantity is set to 0 or less, the item is removed from the cart.
   *
   * @param itemId - The unique key of the item to update.
   * @param quantity - The new quantity for the item.
   */
  updateItemQuantity(itemId: string, quantity: number): void {
    const item = this.items.find((i) => i.key === itemId);
    if (item) {
      item.value.quantity = quantity;
      if (item.value.quantity <= 0) {
        this.removeItem(itemId);
      }
    }
  }

  /**
   * Clears all items from the cart.
   */
  clearCart(): void {
    this.items = [];
  }

  /**
   * Calculates the total number of items in the cart.
   *
   * @returns The sum of quantities of all items.
   */
  get totalItems(): number {
    return this.items.reduce((total, item) => total + item.value.quantity, 0);
  }

  /**
   * Calculates the total price of all items in the cart.
   *
   * @returns The sum of (price * quantity) for all items.
   */
  get totalPrice(): number {
    return this.items.reduce(
      (total, item) => total + item.value.price * item.value.quantity,
      0
    );
  }

  /**
   * Copies properties from another Cart instance.
   *
   * @param other - The other Cart instance to copy properties from.
   */
  copyPropertiesFrom(other: Cart): void {
    this.id = other.id;
    this.merchantId = other.merchantId;
  }

  /**
   * Serializes the cart to a JSON-compatible object.
   *
   * @returns An object representation of the cart.
   */
  toJSON(): any {
    return {
      id: this.id,
      merchantId: this.merchantId,
      items: this.items,
      // type: "Cart",
    };
  }

  /**
   * Re-hydrates a Cart instance (or a specific subclass) from a JSON object.
   *
   * This factory method determines the specific Cart subclass to instantiate
   * based on the `type` property in the data.
   *
   * @param data - The raw data object (e.g., from JSON or LocalStorage).
   * @returns An instance of `RetailCart`, `RestaurantCart`, or `ServiceCart`, or `undefined` if the type is unknown.
   */
  static fromJSON(data: any): Cart | undefined {
    // eslint-disable-next-line
    const { RetailCart } = require("./retailCart");
    // eslint-disable-next-line
    const { RestaurantCart } = require("./restaurantCart");
    // eslint-disable-next-line
    const { ServiceCart } = require("./serviceCart");

    switch (data.type) {
      case CartType.RETAIL_CART:
        return new RetailCart(data);
      case CartType.RESTAURANT_CART:
        return new RestaurantCart(data);
      case CartType.SERVICE_CART:
        return new ServiceCart(data);
    }
    // If no matching type is found, return undefined
    return undefined;
  }

  // Protected Static Helpers for Subclasses

  /**
   *  Helper to notify React that the cart mutated
   *  to send an event to the native CMA app
   */
  private static _notifyCartUpdated(cartData: CartItem[]): void {
    if (globalThis.window !== undefined) {
      console.log("Dispatching event with items: ", cartData);
      globalThis.window.dispatchEvent(
        new CustomEvent("local-cart-updated", { detail: cartData })
      );
    }
  }

  protected static _addToStorage(key: LocalStorageKey, item: CartItem) {
    const cart = getFromLocalStorage<CartItem[]>(key, []);
    const existingIndex = cart.findIndex((i) => i.key === item.key);
    if (existingIndex !== -1) {
      cart[existingIndex] = item;
    } else {
      cart.push(item);
    }
    Cart._updateVersion(key);
    saveToLocalStorage(key, cart);
    if (item.value.setIsAddtocartClicked) {
      item.value.setIsAddtocartClicked(false);
    }
    Cart._notifyCartUpdated(cart);
  }

  protected static _getFromStorage<T extends CartItem>(
    key: LocalStorageKey
  ): T[] {
    const cart = getFromLocalStorage(key, []);
    return cart as T[];
  }

  protected static _clearStorage(key: LocalStorageKey): void {
    Cart._updateVersion(key);
    removeFromLocalStorage(key);
    Cart._notifyCartUpdated([]);
  }

  protected static _removeFromStorage(
    key: LocalStorageKey,
    cartIdToRemove: string
  ) {
    const currentCart = getFromLocalStorage<CartItem[]>(key, []);
    const updatedCart = currentCart.filter(
      (item) => item.key !== cartIdToRemove
    );
    Cart._updateVersion(key);
    saveToLocalStorage(key, updatedCart);
    Cart._notifyCartUpdated(updatedCart);
  }

  private static _updateVersion(key: LocalStorageKey) {
    const versionKey = `${key}_TIMESTAMP` as unknown as LocalStorageKey;
    saveToLocalStorage(versionKey, Date.now());
  }

  static removeHashFromURL(
    hash: string,
    pathName: string,
    searchParams?: ReadonlyURLSearchParams
  ) {
    if (hash !== undefined) {
      const path = `${pathName}${searchParams?.size ? `?${searchParams}` : ""}`;
      window.history.replaceState({}, "", path);
    }
  }

  static triggerCartSync(isRunningTabsEnabled: boolean, tableData: any) {
    isRunningTabsEnabled &&
      tableData &&
      window.dispatchEvent(
        new StorageEvent("storage", { key: LocalStorageKey.CURRENT_CART_EVENT })
      );
  }
}
